/**
 * Stateful Macro Coordinator Stores
 *
 * Reactive store carrying the current native macro run so UI / debugging
 * surfaces can subscribe without poking at the coordinator's private fields.
 */

import type { MacroRuntimeMode, MacroTrigger } from '@playcastdotio/Messaging';
import { makeStateStore } from '@playcastdotio/StateStore';

/**
 * Public, read-only snapshot of the coordinator's run state.
 *
 * - `active`: true from campaign launch until the native run reaches a
 *   terminal outcome or is cancelled by a campaign switch / game close.
 * - `trigger`: which locked tree is running; null when idle.
 * - `runId`: host-owned id for the current run; used to drop stale outcomes.
 * - `mode`: the runtime mode the run was started with.
 */
export type StatefulMacroRunState = {
  active: boolean;
  trigger: MacroTrigger | null;
  runId: string | null;
  mode: MacroRuntimeMode | null;
};

export const IDLE_MACRO_RUN_STATE: StatefulMacroRunState = { active: false, trigger: null, runId: null, mode: null };

export const createStatefulMacroRunStore = () => makeStateStore<StatefulMacroRunState>(IDLE_MACRO_RUN_STATE);

export type StatefulMacroRunStore = ReturnType<typeof createStatefulMacroRunStore>;
